/**
 * Cliente HTTP de settings globales — Fase 11g.
 *
 * GET `/api/settings` es público (lo necesita la página de NOTAMs
 * que también lo es). PUT `/api/settings/discord` requiere admin+.
 */

import type {
  AppSettingsResponse,
  DiscordSettings,
  DiscordSettingsPayload,
} from '@51esc/shared';

export type FetchSettingsResult =
  | { ok: true; data: AppSettingsResponse }
  | { ok: false; message: string };

export type UpdateDiscordResult =
  | { ok: true; data: DiscordSettings }
  | { ok: false; message: string };

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body && typeof body.error === 'string') return body.error;
  } catch {
    /* swallow */
  }
  return `El servidor respondió con ${String(res.status)}.`;
}

export async function fetchAppSettings(): Promise<FetchSettingsResult> {
  let res: Response;
  try {
    res = await fetch('/api/settings', { credentials: 'include' });
  } catch (err) {
    console.error('[settings] fetch network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) return { ok: false, message: await readError(res) };
  const body = (await res.json().catch(() => null)) as
    | AppSettingsResponse
    | null;
  if (!body || typeof body !== 'object' || !body.discord) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, data: body };
}

export async function updateDiscordSettings(
  payload: DiscordSettingsPayload,
): Promise<UpdateDiscordResult> {
  let res: Response;
  try {
    res = await fetch('/api/settings/discord', {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.error('[settings] update discord network err', err);
    return { ok: false, message: 'No se pudo contactar con el servidor.' };
  }
  if (!res.ok) return { ok: false, message: await readError(res) };
  const body = (await res.json().catch(() => null)) as
    | { discord: DiscordSettings }
    | null;
  if (!body || !body.discord) {
    return { ok: false, message: 'Respuesta del servidor inválida.' };
  }
  return { ok: true, data: body.discord };
}
